import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFaq, deleteFaq, listFaqs, updateFaq, type Faq } from "@/integrations/backend/faqs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreVertical, Edit3, Trash2 } from "lucide-react";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";

type FAQ = Faq;

const faqsQueryKey = queryKeys.faqs.all;

export default function FAQView() {
  const queryClient = useQueryClient();
  const faqsQuery = useQuery({
    queryKey: faqsQueryKey,
    queryFn: listFaqs,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...faqsQueryKey]),
  });
  const faqs = useMemo(() => {
    const data = faqsQuery.data ?? [];
    return [...data].sort((a, b) => (a.created_at > b.created_at ? -1 : 1));
  }, [faqsQuery.data]);

  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [editing, setEditing] = useState<FAQ | null>(null);

  const save = async () => {
    if (!question.trim() || !answer.trim()) return;
    if (editing) {
      await updateFaq(editing.id, { question, answer });
    } else {
      await createFaq({ question, answer });
    }
    setOpen(false);
    setQuestion("");
    setAnswer("");
    setEditing(null);
    await queryClient.invalidateQueries({ queryKey: faqsQueryKey });
  };

  const openEdit = (faq: FAQ) => {
    setEditing(faq);
    setQuestion(faq.question);
    setAnswer(faq.answer);
    setOpen(true);
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this FAQ?")) return;
    await deleteFaq(id);
    await queryClient.invalidateQueries({ queryKey: faqsQueryKey });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">FAQs</h1>
          <p className="text-gray-600">Manage Frequently Asked Questions for the marketing site.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>Add FAQ</Button>
          </DialogTrigger>
          <DialogContent className="bg-white text-black">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-1">
              <Input placeholder="Question" value={question} onChange={(e) => setQuestion(e.target.value)} className="bg-white text-black placeholder:text-gray-500" />
              <Textarea placeholder="Answer" value={answer} onChange={(e) => setAnswer(e.target.value)} className="min-h-[140px] bg-white text-black placeholder:text-gray-500" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setOpen(false); setEditing(null); }} className="text-black border-gray-300 hover:bg-gray-100">
                Cancel
              </Button>
              <Button onClick={save}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All FAQs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Question</TableHead>
                  <TableHead>Answer</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {faqs.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="font-medium">{f.question}</TableCell>
                    <TableCell>{f.answer}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon" aria-label="Actions">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(f)}>
                            <Edit3 className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => remove(f.id)} className="text-destructive focus:text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
